<template>
    <fieldset>
        <legend v-if="label" class="sr-only">{{ label }}</legend>
        <RadioGroupRoot
            :model-value="modelValue?.toString()"
            @update:model-value="(v) => emit('update:modelValue', Number(v))"
            @keydown.capture="handleKeydown"
            @click.capture="handleClick">
            <div class="flex items-center justify-between gap-2">
                <RadioGroupItem
                    v-for="point in SCALE_POINTS"
                    :key="point.value"
                    :value="point.value.toString()"
                    :disabled="disabled"
                    :aria-label="point.label"
                    :class="[
                        'size-8 rounded-full border-2 transition-all duration-150 cursor-pointer outline-none',
                        'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-valence-primary-500',
                        modelValue === point.value ? `${point.border} ${point.bg}` : `${point.border} bg-transparent hover:scale-110`,
                    ]" />
            </div>
        </RadioGroupRoot>
        <div class="flex justify-between mt-2">
            <span class="details text-gray-400">Disagree</span>
            <span class="details text-gray-400">Agree</span>
        </div>
    </fieldset>
</template>

<script lang="ts">
export interface Props {
    modelValue?: number;
    disabled?: boolean;
    label?: string;
}

/** Exported so a caller can enumerate the valid answers rather than restating the scale. */
export const SCALE_POINTS = [
    { value: -3, label: "Strongly disagree", border: "border-valence-primary-600", bg: "bg-valence-primary-600" },
    { value: -2, label: "Disagree", border: "border-valence-primary-500", bg: "bg-valence-primary-500" },
    { value: -1, label: "Slightly disagree", border: "border-valence-primary-300", bg: "bg-valence-primary-300" },
    { value: 0, label: "Neutral", border: "border-gray-300", bg: "bg-gray-300" },
    { value: 1, label: "Slightly agree", border: "border-valence-primary-300", bg: "bg-valence-primary-300" },
    { value: 2, label: "Agree", border: "border-valence-primary-500", bg: "bg-valence-primary-500" },
    { value: 3, label: "Strongly agree", border: "border-valence-primary-600", bg: "bg-valence-primary-600" },
] as const;
</script>

<script setup lang="ts">
import { RadioGroupItem, RadioGroupRoot } from "reka-ui";
import { nextTick } from "vue";

defineProps<Props>();

const emit = defineEmits<{
    "update:modelValue": [value: number];
    advance: [columnIndex: number];
}>();

function handleKeydown(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === " ") {
        const target = event.target as HTMLElement;
        if (target.getAttribute("role") === "radio") {
            event.preventDefault();
            event.stopPropagation();
            const value = target.getAttribute("value");
            if (value === null) {
                return;
            }
            emit("update:modelValue", Number(value));
            const columnIndex = SCALE_POINTS.findIndex((p) => p.value === Number(value));
            nextTick(() => emit("advance", columnIndex >= 0 ? columnIndex : 0));
        }
    }
}

function handleClick(event: MouseEvent) {
    // reka-ui fires a programmatic click on arrow key, this blocks unless its from a user agent
    if (!event.isTrusted) {
        return;
    }
    const target = (event.target as HTMLElement).closest('[role="radio"]');
    if (!target) {
        return;
    }
    const value = target.getAttribute("value");
    if (value === null) {
        return;
    }
    const columnIndex = SCALE_POINTS.findIndex((p) => p.value === Number(value));
    nextTick(() => emit("advance", columnIndex >= 0 ? columnIndex : 0));
}
</script>
